import React, { useState, useEffect, useCallback } from 'react';
import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import ActionSelector from './components/ActionSelector';
import RangeGrid from './components/RangeGrid';
import RangeForm from './components/RangeForm';
import RangeList from './components/RangeList';
import CheatSheet from './components/CheatSheet';
import PWAUpdatePrompt from './components/PWAUpdatePrompt';
import { rangesApi, authApi, generateEmptyGrid, DEFAULT_ACTIONS, ACTION_COLORS } from './api/rangesApi';
import './App.css';

function App() {
  // Авторизация
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  // Режим приложения: 'editor' или 'cheatsheet'
  const [mode, setMode] = useState('cheatsheet');
  
  // Состояние формы
  const [name, setName] = useState('');
  const [position, setPosition] = useState('BTN');
  const [vsPositions, setVsPositions] = useState([]);  // Массив позиций противника
  const [comment, setComment] = useState('');
  const [stackFrom, setStackFrom] = useState(0);
  const [stackTo, setStackTo] = useState(100);
  
  // Состояние действий и сетки
  const [actions, setActions] = useState(DEFAULT_ACTIONS);
  const [selectedAction, setSelectedAction] = useState('raise');
  const [grid, setGrid] = useState(generateEmptyGrid());
  
  // Состояние списка и редактирования
  const [ranges, setRanges] = useState([]);
  const [selectedRangeId, setSelectedRangeId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Проверка авторизации при загрузке
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      if (token && savedUser) {
        try {
          const userData = await authApi.getMe();
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        } catch {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setIsAuthLoading(false);
    };
    
    checkAuth();
  }, []);

  // Загрузка списка диапазонов
  const loadRanges = useCallback(async () => {
    if (!user) return;
    try {
      const data = await rangesApi.getAll();
      setRanges(data);
    } catch (error) {
      console.error('Ошибка загрузки:', error);
    }
  }, [user]);

  useEffect(() => {
    loadRanges();
  }, [loadRanges]);

  // Обработчик входа
  const handleLogin = (userData) => {
    setUser(userData);
  };

  // Обработчик выхода
  const handleLogout = () => {
    authApi.logout();
    setUser(null);
    setRanges([]);
    setSelectedRangeId(null);
    handleNewRange();
  };

  // Показать сообщение
  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  // Проверка прав на запись
  const canWrite = user && !user.read_only;

  // Выбор диапазона из списка
  const handleSelectRange = async (id) => {
    setIsLoading(true);
    try {
      const range = await rangesApi.getById(id);
      setSelectedRangeId(id);
      setName(range.name);
      setPosition(range.position);
      setVsPositions(range.vs_positions || []);
      setComment(range.comment);
      setStackFrom(range.stack_from || 0);
      setStackTo(range.stack_to || 100);
      // Применяем фиксированные цвета из ACTION_COLORS
      const actionsWithFixedColors = range.actions.map(action => ({
        ...action,
        color: ACTION_COLORS[action.id] || action.color
      }));
      setActions(actionsWithFixedColors);
      setGrid(range.grid);
      showMessage('Диапазон загружен', 'success');
    } catch (error) {
      showMessage('Ошибка загрузки диапазона', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Создать новый диапазон
  const handleNewRange = () => {
    setSelectedRangeId(null);
    setName('');
    setPosition('BTN');
    setVsPositions([]);
    setComment('');
    setStackFrom(0);
    setStackTo(100);
    setActions(DEFAULT_ACTIONS);
    setGrid(generateEmptyGrid());
  };

  // Сохранить диапазон
  const handleSave = async () => {
    if (!canWrite) {
      showMessage('У вас нет прав на редактирование', 'error');
      return;
    }
    
    if (!name.trim()) {
      showMessage('Введите название диапазона', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const rangeData = {
        name,
        position,
        vs_positions: vsPositions,
        comment,
        stack_from: stackFrom,
        stack_to: stackTo,
        actions,
        grid,
      };

      if (selectedRangeId) {
        await rangesApi.update(selectedRangeId, rangeData);
        showMessage('Диапазон обновлён', 'success');
      } else {
        const newRange = await rangesApi.create(rangeData);
        setSelectedRangeId(newRange.id);
        showMessage('Диапазон сохранён', 'success');
      }
      
      await loadRanges();
    } catch (error) {
      showMessage('Ошибка сохранения', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Удалить диапазон
  const handleDelete = async (id) => {
    if (!canWrite) {
      showMessage('У вас нет прав на удаление', 'error');
      return;
    }
    
    if (!window.confirm('Удалить диапазон?')) return;
    
    try {
      await rangesApi.delete(id);
      if (selectedRangeId === id) {
        handleNewRange();
      }
      await loadRanges();
      showMessage('Диапазон удалён', 'success');
    } catch (error) {
      showMessage('Ошибка удаления', 'error');
    }
  };

  // Клонировать диапазон
  const handleClone = async (id) => {
    if (!canWrite) {
      showMessage('У вас нет прав на создание', 'error');
      return;
    }
    
    try {
      const range = await rangesApi.getById(id);
      
      // Применяем фиксированные цвета
      const actionsWithFixedColors = range.actions.map(action => ({
        ...action,
        color: ACTION_COLORS[action.id] || action.color
      }));
      
      // Создаём копию с новым названием
      const cloneData = {
        name: `${range.name} (копия)`,
        position: range.position,
        vs_positions: range.vs_positions || [],
        comment: range.comment,
        stack_from: range.stack_from || 0,
        stack_to: range.stack_to || 100,
        actions: actionsWithFixedColors,
        grid: range.grid,
      };
      
      const newRange = await rangesApi.create(cloneData);
      await loadRanges();
      
      // Выбираем клонированный диапазон
      setSelectedRangeId(newRange.id);
      setName(cloneData.name);
      setPosition(cloneData.position);
      setVsPositions(cloneData.vs_positions);
      setComment(cloneData.comment);
      setStackFrom(cloneData.stack_from);
      setStackTo(cloneData.stack_to);
      setActions(actionsWithFixedColors);
      setGrid(cloneData.grid);
      
      showMessage('Диапазон клонирован', 'success');
    } catch (error) {
      showMessage('Ошибка клонирования', 'error');
    }
  };

  // Очистить сетку
  const handleClearGrid = () => {
    if (!canWrite) {
      showMessage('У вас нет прав на редактирование', 'error');
      return;
    }
    setGrid(generateEmptyGrid());
  };

  // Загрузка диапазона для шпаргалки
  const loadRangeById = useCallback(async (id) => {
    return await rangesApi.getById(id);
  }, []);

  // Загрузка...
  if (isAuthLoading) {
    return (
      <div className="app loading-screen">
        <PWAUpdatePrompt />
        <div className="loading-spinner">♠️</div>
        <p>Загрузка...</p>
      </div>
    );
  }

  // Страница входа
  if (!user) {
    return (
      <>
        <PWAUpdatePrompt />
        <Login onLogin={handleLogin} />
      </>
    );
  }

  // Панель администратора
  if (showAdminPanel && user.is_admin) {
    return (
      <div className="app">
        <PWAUpdatePrompt />
        <div className="user-bar">
          <div className="user-info-bar">
            <span className="user-welcome">👤 {user.username}</span>
            {user.is_admin && <span className="user-badge admin">Админ</span>}
            {user.read_only && <span className="user-badge readonly">Только чтение</span>}
          </div>
          <div className="user-actions">
            <button 
              className="btn btn-secondary"
              onClick={() => setShowAdminPanel(false)}
            >
              ← Назад
            </button>
            <button className="btn btn-logout" onClick={handleLogout}>
              Выйти
            </button>
          </div>
        </div>
        <AdminPanel onClose={() => setShowAdminPanel(false)} />
      </div>
    );
  }

  // Режим шпаргалки
  if (mode === 'cheatsheet') {
    return (
      <div className="app">
        <PWAUpdatePrompt />
        <div className="user-bar">
          <div className="user-info-bar">
            <span className="user-welcome">👤 {user.username}</span>
            {user.is_admin && <span className="user-badge admin">Админ</span>}
            {user.read_only && <span className="user-badge readonly">Только чтение</span>}
          </div>
          <div className="user-actions">
            {user.is_admin && (
              <button 
                className="btn btn-admin"
                onClick={() => setShowAdminPanel(true)}
              >
                👥 Пользователи
              </button>
            )}
            <button className="btn btn-logout" onClick={handleLogout}>
              Выйти
            </button>
          </div>
        </div>

        <div className="mode-switcher">
          <button 
            className={`mode-btn ${mode === 'cheatsheet' ? 'active' : ''}`}
            onClick={() => setMode('cheatsheet')}
          >
            🎯 Шпаргалка
          </button>
          <button 
            className={`mode-btn ${mode === 'editor' ? 'active' : ''}`}
            onClick={() => setMode('editor')}
          >
            ✏️ Редактор
          </button>
        </div>
        <CheatSheet ranges={ranges} onLoadRange={loadRangeById} />
      </div>
    );
  }

  // Режим редактора
  return (
    <div className="app">
      <PWAUpdatePrompt />
      <div className="user-bar">
        <div className="user-info-bar">
          <span className="user-welcome">👤 {user.username}</span>
          {user.is_admin && <span className="user-badge admin">Админ</span>}
          {user.read_only && <span className="user-badge readonly">Только чтение</span>}
        </div>
        <div className="user-actions">
          {user.is_admin && (
            <button 
              className="btn btn-admin"
              onClick={() => setShowAdminPanel(true)}
            >
              👥 Пользователи
            </button>
          )}
          <button className="btn btn-logout" onClick={handleLogout}>
            Выйти
          </button>
        </div>
      </div>

      <div className="mode-switcher">
        <button 
          className={`mode-btn ${mode === 'cheatsheet' ? 'active' : ''}`}
          onClick={() => setMode('cheatsheet')}
        >
          🎯 Шпаргалка
        </button>
        <button 
          className={`mode-btn ${mode === 'editor' ? 'active' : ''}`}
          onClick={() => setMode('editor')}
        >
          ✏️ Редактор
        </button>
      </div>

      <header className="app-header">
        <div className="logo">
          <span className="logo-icon">♠️</span>
          <h1>Poker Ranges</h1>
        </div>
        <div className="header-actions">
          {canWrite && (
            <>
              <button 
                className="btn btn-secondary"
                onClick={handleClearGrid}
              >
                Очистить
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Сохранение...' : (selectedRangeId ? 'Обновить' : 'Сохранить')}
              </button>
            </>
          )}
          {!canWrite && (
            <span className="readonly-notice">🔒 Режим просмотра</span>
          )}
        </div>
      </header>

      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <main className="app-main">
        <aside className="sidebar">
          <RangeList
            ranges={ranges}
            selectedId={selectedRangeId}
            onSelect={handleSelectRange}
            onDelete={canWrite ? handleDelete : null}
            onClone={canWrite ? handleClone : null}
            onNew={canWrite ? handleNewRange : null}
            readOnly={!canWrite}
          />
        </aside>

        <section className="editor">
          <RangeForm
            name={name}
            position={position}
            vsPositions={vsPositions}
            comment={comment}
            stackFrom={stackFrom}
            stackTo={stackTo}
            onNameChange={canWrite ? setName : () => {}}
            onPositionChange={canWrite ? setPosition : () => {}}
            onVsPositionsChange={canWrite ? setVsPositions : () => {}}
            onCommentChange={canWrite ? setComment : () => {}}
            onStackFromChange={canWrite ? setStackFrom : () => {}}
            onStackToChange={canWrite ? setStackTo : () => {}}
            readOnly={!canWrite}
          />
          
          <div className="grid-and-actions">
            <div className={`grid-wrapper ${isLoading ? 'loading' : ''}`}>
              <RangeGrid
                grid={grid}
                actions={actions}
                selectedAction={selectedAction}
                onGridChange={canWrite ? setGrid : () => {}}
                readOnly={!canWrite}
              />
            </div>

            <ActionSelector
              actions={actions}
              onActionsChange={canWrite ? setActions : () => {}}
              selectedAction={selectedAction}
              onSelectAction={setSelectedAction}
              readOnly={!canWrite}
            />
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;
